import { Container } from './primitives/Misc.js';
import './Agents.css';

/**
 * **The agent layer, on the public site.**
 *
 * ── The claim being made, and the ones deliberately not made ──────────────────────────────────
 *
 * The category is *agent reliability*, and the honest version of it is narrow: an agent reading raw
 * CRM will confidently pick one of three duplicate contacts, and we remove that by deciding which
 * record is true before the agent ever sees one. That is a statement about the **input**.
 *
 * ⚠️ **It is never phrased as a statement about the model.** "Stops hallucination" would be a claim
 * about somebody else's system that we cannot support, and
 * [messaging §7](../../../../docs/landing/messaging.md) forbids exactly that shape. What is said
 * here is what the product does: the agent never sees an unresolved duplicate.
 *
 * ⚠️ **`<100ms` carries the words "design target", in the same sentence, every time.** That is the
 * single permitted exception in §7, added 2026-08-16, and it holds only while all three of its
 * conditions do — the label is adjacent, a harness exists that could disprove it
 * (`scripts/measure-mcp-latency.mjs`), and it stops being a target the moment it is measured. It has
 * **not** been measured; `docs/process/mcp-latency-and-conformance-2026-08-16.md` is the record.
 */

/** What an agent gets that it does not get from a CRM API. Each one is a built behaviour. */
const GUARANTEES: readonly string[] = [
  'One record per customer — an agent never reads a duplicate you have not resolved',
  'Only decided state: a merge a human has not approved is never served as fact',
  'Lineage on every answer, so the agent can say which records it came from',
  'The true age of the data on every answer, never a figure presented as current',
  'Field-level masking, enforced by us rather than by your prompt',
  'Read and propose — nothing an agent does can change a record on its own',
  /* ⚠️ Deliberately last and deliberately concrete: this is the differentiator a buyer with an
     audit obligation reads first, and burying it under the speed line would be the wrong order. */
  'A receipt for every query, including the ones we refuse',
];

export function Agents() {
  return (
    <Container id="agents" className="land-section">
      <div className="land-agents__grid">
        <div>
          <p className="land-agents__eyebrow">For AI support and sales agents</p>
          <h2 className="land-agents__title">Your agents are only as good as the record they read.</h2>
          <p className="land-agents__lede">
            An agent pointed at a raw CRM will pick one of three duplicate contacts and answer with
            total confidence. Dedupr decides which record is true — under human approval — and serves
            that one to your agents over the <strong>Model Context Protocol</strong>, the open standard
            Claude, n8n, Make, Agentforce and the voice platforms already speak — no SDK to adopt and
            no connector to build.
          </p>
          <ul className="land-checklist">
            {GUARANTEES.map((item) => (
              <li key={item}>
                <span className="land-checklist__mark">✓</span>
                {item}
              </li>
            ))}
          </ul>
          <p className="land-agents__note">
            {/* ⚠️ The label is in the sentence, not a footnote — §7's first condition. A reader takes
                the number and leaves an asterisk behind. */}
            {/* ⚠️ **`<100ms` was removed on 2026-08-16 because it was MEASURED and is not true.**
                Against production: cold p50 150–482 ms per tool, ~152 ms on a cache hit. A design
                target that measurement has already contradicted is not a statement of intent, it is
                a claim we know to be false — so the number here is the measured one, and it names
                the date and the conditions rather than standing alone. Record:
                docs/process/mcp-latency-and-conformance-2026-08-16.md. */}
            Answers are served from the edge where one is cached. Measured against production on
            16&nbsp;August&nbsp;2026, a first query returns in{' '}
            <strong>150–480&nbsp;ms</strong> depending on the tool, and a repeat of the same question
            in about <strong>170&nbsp;ms</strong>.
          </p>
        </div>

        <aside className="land-agents__panel" aria-label="What an agent receives">
          <p className="land-agents__panel-title">What an agent receives</p>
          {/* Not a screenshot: the shape of a real answer, which is the thing a technical buyer
              wants to see and the thing a marketing image would blur. */}
          <pre className="land-agents__answer">{`{
  "surface": "contacts@v2",
  "as_of": "2026-08-16T09:12:04Z",
  "staleness_seconds": 118,
  "records": [
    {
      "id": "ent_7f31",
      "values": { "full_name": "Ana Ozols", "email": "tok_9c4e…" },
      "lineage": {
        "dedupr_source_systems": "salesforce,hubspot",
        "dedupr_source_record_ids": "0031x…,4210…"
      }
    }
  ]
}`}</pre>
          <p className="land-agents__panel-note">
            One record, not three. Masked where you said so. Traceable to the systems it came from.
          </p>
        </aside>
      </div>
    </Container>
  );
}
